import * as React from 'react';
import { cn } from '@/lib/utils';

/**
 * JP Skeleton — loading placeholder per design system v1.2
 *
 * Pulses on Surface 3 (utility) so placeholders read one layer above
 * the card they load into. SkeletonButton is a pill to match the JP
 * Button silhouette. Respects prefers-reduced-motion via Tailwind's
 * animate-pulse.
 *
 * Both themes resolve via jp-* tokens — no `dark:` variants.
 */

interface SkeletonProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Width of the skeleton. Can be a string (e.g., '100%', '200px') or number (px) */
  width?: string | number;
  /** Height of the skeleton. Can be a string (e.g., '100%', '20px') or number (px) */
  height?: string | number;
  /** Make it a circle (sets border-radius to 50%) */
  circle?: boolean;
  /** Border radius preset */
  rounded?: 'none' | 'sm' | 'md' | 'lg' | 'full';
}

const radiusMap = {
  none: '0',
  sm: '8px',
  md: '12px',
  lg: '16px',
  full: '9999px',
} as const;

const Skeleton = React.forwardRef<HTMLDivElement, SkeletonProps>(
  (
    {
      className,
      width,
      height,
      circle,
      rounded = 'md',
      style,
      ...props
    },
    ref
  ) => {
    return (
      <div
        ref={ref}
        className={cn(
          'animate-pulse',
          'bg-jp-bg-utility',
          className
        )}
        style={{
          width: typeof width === 'number' ? `${width}px` : width,
          height: typeof height === 'number' ? `${height}px` : height,
          borderRadius: circle ? '50%' : radiusMap[rounded],
          ...style,
        }}
        aria-hidden="true"
        {...props}
      />
    );
  }
);

Skeleton.displayName = 'Skeleton';

const SkeletonCard = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'rounded-2xl overflow-hidden',
        'bg-jp-bg-card border border-jp-line-hairline',
        className
      )}
      {...props}
    >
      {/* Image placeholder */}
      <Skeleton height={180} width="100%" rounded="none" />
      {/* Content */}
      <div className="p-4 space-y-3">
        <Skeleton height={20} width="70%" rounded="sm" />
        <Skeleton height={14} width="100%" rounded="sm" />
        <Skeleton height={14} width="50%" rounded="sm" />
      </div>
    </div>
  )
);
SkeletonCard.displayName = 'SkeletonCard';

export {
  Skeleton,
  SkeletonCard,
};
